import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stack,
  styled,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import PhoneForwardedIcon from "@mui/icons-material/PhoneForwarded";
import LanguageIcon from "@mui/icons-material/Language";
import BgTitle from "components/title/BgTitle";
import MailIcon from "@mui/icons-material/Mail";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import unicsLogoImg from "images/GroupCompany/unics.png";
import iscLogoImg from "images/GroupCompany/isc.gif";

const images = [unicsLogoImg, iscLogoImg];

const CompanyLogo = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const StyledListItem = styled(ListItem)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const InfoButton = styled(IconButton)(({ theme }) => ({
  padding: "8px",
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
  },
}));

const AffiliateCompanyMobileView = () => {
  const { t } = useTranslation();
  const cards = t("affiliate_company_items", { returnObjects: true });

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <BgTitle text={t("affiliate_company_headline")} />
      </Box>

      <List sx={{ bgcolor: "#F5F5F5", borderRadius: 1 }}>
        {cards.map((card, index) => (
          <React.Fragment key={index}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
            >
              <StyledListItem
                component={motion.div}
                whileTap={{ backgroundColor: "#f5f5f5" }}
              >
                <ListItemAvatar sx={{ mr: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: "white",
                      border: "1px solid #eee",
                    }}
                    variant="rounded"
                  >
                    <CompanyLogo src={images[index]} alt={card.title} />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        color: "black",
                        fontSize: "0.8rem",
                      }}
                    >
                      {card.title}
                    </Typography>
                  }
                  secondary={
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FmdGoodIcon
                          sx={{
                            fontSize: 16,
                            color: "text.secondary",
                          }}
                        />
                        <Typography
                          fontSize={"0.7rem"}
                          variant="body2"
                          color="text.secondary"
                        >
                          {card.address}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneForwardedIcon
                          sx={{
                            fontSize: 16,
                            color: "text.secondary",
                          }}
                        />
                        <Typography
                          fontSize={"0.7rem"}
                          variant="body2"
                          color="text.secondary"
                        >
                          {card.phone}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MailIcon
                          sx={{
                            fontSize: 16,
                            color: "text.secondary",
                          }}
                        />
                        <Typography
                          fontSize={"0.7rem"}
                          variant="body2"
                          color="text.secondary"
                        >
                          {card.mail}
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                />

                <InfoButton
                  edge="end"
                  onClick={() => window.open(card.link)}
                  sx={{ ml: 2 }}
                >
                  <LanguageIcon />
                </InfoButton>
              </StyledListItem>
            </motion.div>
            {index < cards.length - 1 && (
              <Divider
                sx={{
                  mx: 2,
                  borderColor: "divider",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </List>
    </Container>
  );
};

export default AffiliateCompanyMobileView;
